import type {
  ArenaConfig,
  BotRosterConfig,
  BotTargetPreference,
} from "../config/gameplay-config";
import type { RandomSource } from "./seeded-random";

export interface BotPosition {
  readonly x: number;
  readonly y: number;
}

export interface BotCollisionState {
  readonly radius: number;
}

export interface BotGameplayTraits {
  readonly aggression: number;
  readonly challenger: boolean;
  readonly level: number;
  readonly perceptionRadius: number;
  readonly steeringNoise: number;
  readonly targetPreference: BotTargetPreference;
}

export interface BotCosmeticState {
  readonly accentColor: number;
  readonly bodyColor: number;
  readonly strengthCue: string;
}

export interface BotEntity {
  readonly collision: BotCollisionState;
  readonly cosmetic: BotCosmeticState;
  readonly id: string;
  readonly position: BotPosition;
  readonly traits: BotGameplayTraits;
}

export interface BotExclusionZone {
  readonly position: BotPosition;
  readonly radius: number;
}

export interface BotRosterOptions {
  readonly arena: ArenaConfig;
  readonly config: BotRosterConfig;
  readonly cosmeticRandom: RandomSource;
  readonly gameplayRandom: RandomSource;
  readonly initialExclusions: readonly BotExclusionZone[];
  readonly levelSizes: readonly number[];
  readonly maximumLevel: number;
  readonly playerLevel: number;
  readonly spawnRandom: RandomSource;
}

export const botPalettes = [
  { accentColor: 0xffe066, bodyColor: 0x9b5de5 },
  { accentColor: 0xf7fff7, bodyColor: 0xf15bb5 },
  { accentColor: 0x102a43, bodyColor: 0x00bbf9 },
  { accentColor: 0x102a43, bodyColor: 0xfee440 },
] as const;

const squaredDistance = (left: BotPosition, right: BotPosition): number => {
  const x = left.x - right.x;
  const y = left.y - right.y;
  return x * x + y * y;
};

const isOutsideZone = (
  position: BotPosition,
  radius: number,
  zone: BotExclusionZone,
): boolean =>
  squaredDistance(position, zone.position) >= (radius + zone.radius) ** 2;

const interpolate = (minimum: number, maximum: number, unit: number): number =>
  minimum + (maximum - minimum) * unit;

export const strengthCueForLevel = (
  level: number,
  challenger = false,
): string => `${challenger ? "★ " : ""}Lv ${String(level)}`;

export const getMaximumBotLevel = (
  playerLevel: number,
  config: BotRosterConfig,
): number =>
  Math.min(config.maximumLevel, playerLevel + config.maximumLevelAbovePlayer);

export class BotRoster {
  private readonly bots: BotEntity[] = [];
  private maximumLevel = 0;
  private nextIdentifier = 1;
  private playerLevel = 0;

  public constructor(private readonly options: BotRosterOptions) {
    this.setLevelRange(options.playerLevel, options.maximumLevel);
    while (this.bots.length < options.config.targetActive) {
      this.addBot(undefined, options.initialExclusions);
    }
  }

  public getSnapshot(): readonly BotEntity[] {
    return this.bots.map((bot) => ({
      ...bot,
      collision: { ...bot.collision },
      cosmetic: { ...bot.cosmetic },
      position: { ...bot.position },
      traits: { ...bot.traits },
    }));
  }

  public spawn(level: number, near: BotPosition | null): BotEntity {
    if (
      !Number.isSafeInteger(level) ||
      level < this.options.config.minimumLevel ||
      level > this.maximumLevel
    ) {
      throw new RangeError(
        `Bot level must be an integer from ${String(this.options.config.minimumLevel)} through ${String(this.maximumLevel)}`,
      );
    }
    if (this.bots.length >= this.options.config.maximumActive) {
      throw new Error("Bot roster cannot exceed 20 active characters");
    }
    return this.addBot(level, [], near);
  }

  public replenish(exclusions: readonly BotExclusionZone[]): BotEntity | null {
    if (this.bots.length >= this.options.config.targetActive) {
      return null;
    }
    const selection = this.selectRegeneratedLevel();
    const level = selection.level;
    const radius = this.requireLevelSize(level);
    const position =
      this.findRandomPosition(radius, exclusions, null) ??
      this.findFallbackPosition(radius, exclusions);
    return position === null
      ? null
      : this.commitBot(level, position, selection.challenger);
  }

  public remove(identifier: string): BotEntity {
    const removed = this.bots.find((bot) => bot.id === identifier);
    if (removed === undefined) {
      throw new Error(`Bot roster does not contain ${identifier}`);
    }
    this.bots.splice(this.bots.indexOf(removed), 1);
    return {
      ...removed,
      collision: { ...removed.collision },
      cosmetic: { ...removed.cosmetic },
      position: { ...removed.position },
      traits: { ...removed.traits },
    };
  }

  public setMaximumLevel(maximumLevel: number): void {
    this.setLevelRange(Math.min(this.playerLevel, maximumLevel), maximumLevel);
  }

  public setLevelRange(playerLevel: number, maximumLevel: number): void {
    if (
      !Number.isSafeInteger(maximumLevel) ||
      maximumLevel < this.options.config.minimumLevel ||
      maximumLevel > this.options.config.maximumLevel ||
      maximumLevel > this.options.levelSizes.length
    ) {
      throw new RangeError(
        `Bot maximum level must be an integer from ${String(this.options.config.minimumLevel)} through ${String(Math.min(this.options.config.maximumLevel, this.options.levelSizes.length))}`,
      );
    }
    if (
      !Number.isSafeInteger(playerLevel) ||
      playerLevel < this.options.config.minimumLevel ||
      playerLevel > maximumLevel
    ) {
      throw new RangeError(
        `Player level must be an integer from ${String(this.options.config.minimumLevel)} through ${String(maximumLevel)}`,
      );
    }
    this.playerLevel = playerLevel;
    this.maximumLevel = maximumLevel;
  }

  private addBot(
    requestedLevel: number | undefined,
    exclusions: readonly BotExclusionZone[],
    near: BotPosition | null = null,
  ): BotEntity {
    const selection =
      requestedLevel === undefined
        ? this.selectRegeneratedLevel()
        : {
            challenger: requestedLevel > this.playerLevel,
            level: requestedLevel,
          };
    const level = selection.level;
    const radius = this.requireLevelSize(level);
    const position = this.findRandomPosition(radius, exclusions, near);
    if (position === null) {
      throw new Error("Bot roster could not find a valid spawn position");
    }
    return this.commitBot(level, position, selection.challenger);
  }

  private selectRegeneratedLevel(): {
    readonly challenger: boolean;
    readonly level: number;
  } {
    const challenger =
      this.playerLevel < this.maximumLevel &&
      !this.bots.some((bot) => bot.traits.challenger);
    return {
      challenger,
      level: challenger
        ? this.options.gameplayRandom.nextInteger(
            this.playerLevel + 1,
            this.maximumLevel + 1,
          )
        : this.options.gameplayRandom.nextInteger(
            this.options.config.minimumLevel,
            this.playerLevel + 1,
          ),
    };
  }

  private commitBot(
    level: number,
    position: BotPosition,
    challenger: boolean,
  ): BotEntity {
    const radius = this.requireLevelSize(level);
    const preferenceIndex = this.options.gameplayRandom.nextInteger(
      0,
      this.options.config.targetPreferences.length,
    );
    const targetPreference =
      this.options.config.targetPreferences[preferenceIndex];
    if (targetPreference === undefined) {
      throw new Error("Bot target preferences must not be empty");
    }
    const paletteIndex = this.options.cosmeticRandom.nextInteger(
      0,
      botPalettes.length,
    );
    const palette = botPalettes[paletteIndex];
    if (palette === undefined) {
      throw new Error("Bot palettes must not be empty");
    }
    const bot: BotEntity = {
      collision: { radius },
      cosmetic: {
        ...palette,
        strengthCue: strengthCueForLevel(level, challenger),
      },
      id: `bot-${String(this.nextIdentifier).padStart(4, "0")}`,
      position,
      traits: {
        aggression: interpolate(
          this.options.config.aggressionMinimum,
          this.options.config.aggressionMaximum,
          this.options.gameplayRandom.nextUnit(),
        ),
        challenger,
        level,
        perceptionRadius: interpolate(
          this.options.config.perceptionRadiusMinimum,
          this.options.config.perceptionRadiusMaximum,
          this.options.gameplayRandom.nextUnit(),
        ),
        steeringNoise: interpolate(
          this.options.config.steeringNoiseMinimum,
          this.options.config.steeringNoiseMaximum,
          this.options.gameplayRandom.nextUnit(),
        ),
        targetPreference,
      },
    };
    this.nextIdentifier += 1;
    this.bots.push(bot);
    return bot;
  }

  private createArenaPosition(radius: number): BotPosition {
    const inset =
      this.options.arena.boundaryInset +
      this.options.config.spawnInset +
      radius;
    return {
      x:
        inset +
        this.options.spawnRandom.nextUnit() *
          (this.options.arena.width - inset * 2),
      y:
        inset +
        this.options.spawnRandom.nextUnit() *
          (this.options.arena.height - inset * 2),
    };
  }

  private createNearPosition(center: BotPosition): BotPosition {
    const angle = this.options.spawnRandom.nextUnit() * Math.PI * 2;
    const distance = interpolate(
      this.options.config.debugNearMinimumDistance,
      this.options.config.debugNearMaximumDistance,
      this.options.spawnRandom.nextUnit(),
    );
    return {
      x: center.x + Math.cos(angle) * distance,
      y: center.y + Math.sin(angle) * distance,
    };
  }

  private findRandomPosition(
    radius: number,
    exclusions: readonly BotExclusionZone[],
    near: BotPosition | null,
  ): BotPosition | null {
    for (
      let attempt = 0;
      attempt < this.options.config.maximumSpawnAttemptsPerBot;
      attempt += 1
    ) {
      const position =
        near === null
          ? this.createArenaPosition(radius)
          : this.createNearPosition(near);
      if (this.isValidPosition(position, radius, exclusions)) {
        return position;
      }
    }
    return null;
  }

  private findFallbackPosition(
    radius: number,
    exclusions: readonly BotExclusionZone[],
  ): BotPosition | null {
    const inset =
      this.options.arena.boundaryInset +
      this.options.config.spawnInset +
      radius;
    const width = this.options.arena.width - inset * 2;
    const height = this.options.arena.height - inset * 2;
    for (
      let row = 0;
      row < this.options.config.regenerationFallbackRows;
      row += 1
    ) {
      for (
        let column = 0;
        column < this.options.config.regenerationFallbackColumns;
        column += 1
      ) {
        const position = {
          x:
            inset +
            (width * (column + 0.5)) /
              this.options.config.regenerationFallbackColumns,
          y:
            inset +
            (height * (row + 0.5)) /
              this.options.config.regenerationFallbackRows,
        };
        if (this.isValidPosition(position, radius, exclusions)) {
          return position;
        }
      }
    }
    return null;
  }

  private isValidPosition(
    position: BotPosition,
    radius: number,
    exclusions: readonly BotExclusionZone[],
  ): boolean {
    const inset =
      this.options.arena.boundaryInset +
      this.options.config.spawnInset +
      radius;
    return (
      position.x >= inset &&
      position.x <= this.options.arena.width - inset &&
      position.y >= inset &&
      position.y <= this.options.arena.height - inset &&
      exclusions.every((zone) => isOutsideZone(position, radius, zone)) &&
      this.bots.every((bot) =>
        isOutsideZone(position, radius, {
          position: bot.position,
          radius: bot.collision.radius + this.options.config.minimumSpacing,
        }),
      )
    );
  }

  private requireLevelSize(level: number): number {
    const size = this.options.levelSizes[level - 1];
    if (size === undefined) {
      throw new Error(`Missing collision size for bot level ${String(level)}`);
    }
    return size;
  }
}
